import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig, aboutContent } from '@/mocks/siteConfig';

const FORM_URL = 'https://readdy.ai/api/form/d9q9hhme3oq4jqumtikg';

export default function AboutPage() {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setSubmitStatus('success');
      return;
    }

    formData.delete('phone_alt');
    setSubmitStatus('loading');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
        setSubmitStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setSubmitStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '磁力网站大全',
    url: 'https://www.ciliwangzhan.com',
    description: siteConfig.description,
    foundingDate: siteConfig.foundedYear.toString(),
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteConfig.contact.email,
      contactType: 'customer service',
    },
    sameAs: [],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6 text-center">
                关于磁力网站大全
              </h1>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-12 max-w-3xl mx-auto text-center">
                {aboutContent.brandIntro}
              </p>

              <div className="bg-accent-50 rounded-2xl p-8 md:p-10 mb-12 border border-accent-100">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-flag-fill text-accent-500"></i> 我们的使命
                </h2>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">{aboutContent.mission}</p>
              </div>

              <div className="mb-14">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">核心价值观</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {aboutContent.values.map((v, i) => (
                    <div key={i} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-3">
                        <span className="text-primary-600 font-bold text-lg">{i + 1}</span>
                      </div>
                      <h3 className="font-heading font-bold text-foreground-900 mb-2">{v.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">用户口碑</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {aboutContent.testimonials.map((t, i) => (
                    <div key={i} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">&ldquo;{t.content}&rdquo;</p>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                          <span className="text-accent-600 text-sm font-bold">{t.name[0]}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                          <p className="text-xs text-foreground-500">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/70">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">联系我们</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                        <i className="ri-mail-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">邮箱</p>
                        <p className="text-sm font-medium text-foreground-900">{siteConfig.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                        <i className="ri-wechat-line text-accent-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">微信</p>
                        <p className="text-sm font-medium text-foreground-900">{siteConfig.contact.wechat}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                        <i className="ri-qq-line text-accent-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">QQ群</p>
                        <p className="text-sm font-medium text-foreground-900">{siteConfig.contact.qq}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <form onSubmit={handleSubmit} data-readdy-form="">
                      <div className="space-y-4">
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="您的姓名"
                            className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 outline-none focus:border-primary-300 placeholder:text-foreground-400"
                            required
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="您的邮箱"
                            className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 outline-none focus:border-primary-300 placeholder:text-foreground-400"
                            required
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            placeholder="请描述您的需求或推荐网站..."
                            rows={4}
                            maxLength={500}
                            className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 outline-none focus:border-primary-300 placeholder:text-foreground-400 resize-none"
                            required
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                        </div>
                        <div className="spam-guard">
                          <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>
                        <button
                          type="submit"
                          disabled={submitStatus === 'loading'}
                          className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
                        >
                          {submitStatus === 'loading' ? '提交中...' : '提交信息'}
                        </button>
                        {submitStatus === 'success' && (
                          <p className="text-sm text-accent-600 text-center">感谢您的反馈，我们会尽快回复！</p>
                        )}
                        {submitStatus === 'error' && (
                          <p className="text-sm text-red-500 text-center">{formError}</p>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}